import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Save, Plus, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (n) => `$${Number(n || 0).toLocaleString()}`;

const CatalogEditorTab = () => {
  const [catalog, setCatalog] = useState({ exotic: [], transport: [] });
  const [loading, setLoading] = useState(true);
  const [editingExotic, setEditingExotic] = useState(null); // vehicle_name being edited
  const [editingTransport, setEditingTransport] = useState(null);
  const [newExotic, setNewExotic] = useState(null);
  const [newTransport, setNewTransport] = useState(null);

  const loadCatalog = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/pricing/catalog`);
      setCatalog(await res.json());
    } catch { }
    setLoading(false);
  };

  useEffect(() => { loadCatalog(); }, []);

  const saveExotic = async (vehicle) => {
    try {
      const res = await fetch(`${API}/api/pricing/catalog/exotic/${encodeURIComponent(vehicle.vehicle_name)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiers: vehicle.tiers })
      });
      if (res.ok) { toast.success('Saved'); setEditingExotic(null); loadCatalog(); }
      else toast.error('Failed to save');
    } catch { toast.error('Error'); }
  };

  const addExotic = async () => {
    if (!newExotic?.vehicle_name) return toast.error('Vehicle name required');
    try {
      const res = await fetch(`${API}/api/pricing/catalog/exotic`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newExotic)
      });
      if (res.ok) { toast.success('Added'); setNewExotic(null); loadCatalog(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Error'); }
  };

  const deleteExotic = async (name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    await fetch(`${API}/api/pricing/catalog/exotic/${encodeURIComponent(name)}`, { method: 'DELETE' });
    loadCatalog();
  };

  const saveTransport = async (item) => {
    try {
      const res = await fetch(`${API}/api/pricing/catalog/transport/${item.division}/${encodeURIComponent(item.service)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ retail_hourly: item.retail_hourly, affiliate_hourly: item.affiliate_hourly, member_hourly: item.member_hourly, farmout_hourly: item.farmout_hourly, min_hours: item.min_hours })
      });
      if (res.ok) { toast.success('Saved'); setEditingTransport(null); loadCatalog(); }
      else toast.error('Failed');
    } catch { toast.error('Error'); }
  };

  const updateExoticTier = (vehicleName, hrs, field, value) => {
    setCatalog(prev => ({
      ...prev,
      exotic: prev.exotic.map(v => {
        if (v.vehicle_name !== vehicleName) return v;
        return { ...v, tiers: { ...v.tiers, [hrs]: { ...v.tiers[hrs], [field]: parseFloat(value) || 0 } } };
      })
    }));
  };

  const updateTransportField = (division, service, field, value) => {
    setCatalog(prev => ({
      ...prev,
      transport: prev.transport.map(t => {
        if (t.division !== division || t.service !== service) return t;
        return { ...t, [field]: parseFloat(value) || 0 };
      })
    }));
  };

  return (
    <div data-testid="catalog-editor-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Catalog Editor</h2>
          <p className="text-xs text-gray-500">Live Pricing Database — Edit Without Code</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadCatalog} className="text-xs h-8">
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Exotic Vehicle Catalog */}
      <Card className="p-4 border-gray-200 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900">Exotic Vehicle Catalog</h3>
          <Button size="sm" onClick={() => setNewExotic({ vehicle_name: '', category: 'exotic', tiers: { '4': { retail: 0, affiliate: 0, member: 0, rental_cost: 0, driver_cost: 120 }, '6': { retail: 0, affiliate: 0, member: 0, rental_cost: 0, driver_cost: 180 }, '8': { retail: 0, affiliate: 0, member: 0, rental_cost: 0, driver_cost: 240 }, '12': { retail: 0, affiliate: 0, member: 0, rental_cost: 0, driver_cost: 360 } } })}
            className="text-xs h-7 bg-red-600 hover:bg-red-700" data-testid="add-exotic-btn">
            <Plus className="h-3 w-3 mr-1" /> Add Vehicle
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-gray-200 text-left">
                <th className="py-2 pr-3 font-semibold text-gray-600 min-w-[140px]">Vehicle</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-right">4hr</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-right">6hr</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-right">8hr</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-right">12hr</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-right">Rental (4hr)</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-center w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {catalog.exotic.map(v => {
                const isEditing = editingExotic === v.vehicle_name;
                return (
                  <tr key={v.vehicle_name} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 pr-3 font-semibold text-gray-900">{v.vehicle_name}</td>
                    {['4', '6', '8', '12'].map(hrs => (
                      <td key={hrs} className="py-2 px-2 text-right">
                        {isEditing ? (
                          <Input type="number" value={v.tiers?.[hrs]?.retail || 0}
                            onChange={e => updateExoticTier(v.vehicle_name, hrs, 'retail', e.target.value)}
                            className="h-7 w-20 text-[10px] text-right border-gray-200 ml-auto" />
                        ) : (
                          <span>{f(v.tiers?.[hrs]?.retail || 0)}</span>
                        )}
                      </td>
                    ))}
                    <td className="py-2 px-2 text-right text-gray-500">
                      {isEditing ? (
                        <Input type="number" value={v.tiers?.['4']?.rental_cost || 0}
                          onChange={e => updateExoticTier(v.vehicle_name, '4', 'rental_cost', e.target.value)}
                          className="h-7 w-20 text-[10px] text-right border-gray-200 ml-auto" />
                      ) : (
                        f(v.tiers?.['4']?.rental_cost || 0)
                      )}
                    </td>
                    <td className="py-2 px-2 text-center">
                      {isEditing ? (
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" onClick={() => saveExotic(v)} className="h-6 w-6 p-0 bg-emerald-600 hover:bg-emerald-700"><Save className="h-3 w-3" /></Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditingExotic(null); loadCatalog(); }} className="h-6 w-6 p-0"><span className="text-[10px]">X</span></Button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="outline" onClick={() => setEditingExotic(v.vehicle_name)} className="h-6 text-[10px] px-2">Edit</Button>
                          <Button size="sm" variant="outline" onClick={() => deleteExotic(v.vehicle_name)} className="h-6 w-6 p-0 text-red-500 hover:text-red-700"><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add New Exotic Form */}
        {newExotic && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-xs font-bold mb-2">Add New Exotic Vehicle</h4>
            <div className="grid sm:grid-cols-5 gap-2 mb-2">
              <div className="sm:col-span-2">
                <Input placeholder="Vehicle Name" value={newExotic.vehicle_name}
                  onChange={e => setNewExotic(p => ({ ...p, vehicle_name: e.target.value }))}
                  className="h-8 text-xs border-gray-200" data-testid="new-exotic-name" />
              </div>
              {['4', '6', '8', '12'].map(hrs => (
                <div key={hrs}>
                  <Input type="number" placeholder={`${hrs}hr retail`} value={newExotic.tiers[hrs].retail || ''}
                    onChange={e => setNewExotic(p => ({ ...p, tiers: { ...p.tiers, [hrs]: { ...p.tiers[hrs], retail: parseFloat(e.target.value) || 0 } } }))}
                    className="h-8 text-xs border-gray-200" />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={addExotic} className="bg-red-600 hover:bg-red-700 text-xs h-8">Add Vehicle</Button>
              <Button size="sm" variant="outline" onClick={() => setNewExotic(null)} className="text-xs h-8">Cancel</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Transportation Services Catalog */}
      <Card className="p-4 border-gray-200 mb-4">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Transportation Services Catalog</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-gray-200 text-left">
                <th className="py-2 pr-3 font-semibold text-gray-600">Division</th>
                <th className="py-2 pr-3 font-semibold text-gray-600">Service</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-center">Min Hrs</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-right">Retail/hr</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-right">Affiliate/hr</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-right">Member/hr</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-right">Farm-Out/hr</th>
                <th className="py-2 px-2 font-semibold text-gray-600 text-center w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {catalog.transport.map(t => {
                const key = `${t.division}-${t.service}`;
                const isEditing = editingTransport === key;
                return (
                  <tr key={key} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 pr-3 capitalize font-semibold text-gray-700">{t.division}</td>
                    <td className="py-2 pr-3 text-gray-900">{t.service}</td>
                    <td className="py-2 px-2 text-center">
                      {isEditing ? (
                        <Input type="number" min={1} value={t.min_hours}
                          onChange={e => updateTransportField(t.division, t.service, 'min_hours', e.target.value)}
                          className="h-7 w-14 text-[10px] text-center border-gray-200 mx-auto" />
                      ) : `${t.min_hours}hr`}
                    </td>
                    {['retail_hourly', 'affiliate_hourly', 'member_hourly', 'farmout_hourly'].map(field => (
                      <td key={field} className="py-2 px-2 text-right">
                        {isEditing ? (
                          <Input type="number" value={t[field] || 0}
                            onChange={e => updateTransportField(t.division, t.service, field, e.target.value)}
                            className="h-7 w-16 text-[10px] text-right border-gray-200 ml-auto" />
                        ) : f(t[field] || 0)}
                      </td>
                    ))}
                    <td className="py-2 px-2 text-center">
                      {isEditing ? (
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" onClick={() => saveTransport(t)} className="h-6 w-6 p-0 bg-emerald-600 hover:bg-emerald-700"><Save className="h-3 w-3" /></Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditingTransport(null); loadCatalog(); }} className="h-6 w-6 p-0"><span className="text-[10px]">X</span></Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setEditingTransport(key)} className="h-6 text-[10px] px-2">Edit</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Margin Zone Reference */}
      <Card className="p-4 border-gray-200">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Margin Zone Reference</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="py-2 text-left font-semibold text-gray-600">Division</th>
              <th className="py-2 text-left font-semibold text-gray-600">Metric</th>
              <th className="py-2 text-center font-semibold text-emerald-600">Green</th>
              <th className="py-2 text-center font-semibold text-amber-600">Yellow</th>
              <th className="py-2 text-center font-semibold text-red-600">Red</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-2 font-semibold">Exotic</td>
              <td className="py-2 text-gray-500">Profit (absolute)</td>
              <td className="py-2 text-center"><span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">$300+</span></td>
              <td className="py-2 text-center"><span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">$200-299</span></td>
              <td className="py-2 text-center"><span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-semibold">&lt;$200</span></td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-2 font-semibold">Transport</td>
              <td className="py-2 text-gray-500">Margin (%)</td>
              <td className="py-2 text-center"><span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">30%+</span></td>
              <td className="py-2 text-center"><span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">15-29%</span></td>
              <td className="py-2 text-center"><span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-semibold">&lt;15%</span></td>
            </tr>
          </tbody>
        </table>
        <div className="mt-3 space-y-1 text-[10px] text-gray-500">
          <p>Affiliate pricing: -10% from retail. Member pricing: -15% from retail.</p>
          <p>Member/affiliate discounts cannot breach minimum profit floor.</p>
          <p>Farm-out cost includes vehicle + driver. ACE margin = rate - farm-out cost.</p>
        </div>
      </Card>
    </div>
  );
};

export default CatalogEditorTab;
